import { useState, useEffect, useCallback } from 'react';
import { useLocation, useNavigate, Routes, Route, Navigate } from 'react-router-dom';
import { authService } from '../services/authService';
import { notificationService } from '../services/notificationService';
import { messageService } from '../services/messageService';
import { orderService } from '../services/orderService';
import type { Agent, Notification } from '../types/domain';

// New Intent UI Sidebar
import AppSidebar from '../components/app-sidebar';
import { SidebarProvider, SidebarInset, SidebarTrigger } from '../components/ui/sidebar';
import { OrderSheetProvider } from '../components/orders/OrderSheet';

// Components
import Dashboard from '../components/Dashboard';
import CustomersTable from '../components/customers/CustomersTable';
import CustomerDetail from '../components/CustomerDetail';
import CustomerMap from '../components/CustomerMap';
import EnquiryList from '../components/EnquiryList';
import ViewEnquiry from '../components/ViewEnquiry';
import OfflineStatus from '../components/OfflineStatus';
import { ProgressLoader } from '../components/ProgressLoader';
import { useLoader } from '../contexts/LoaderContext';
import OrderDetail from '../components/OrderDetail';
import OrdersTable from '../components/orders/OrdersTable';
import QuotesTable from '../components/quotes/QuotesTable';
import QuoteBuilder from '../components/quotes/QuoteBuilder';
import QuoteDetail from '../components/quotes/QuoteDetail';
import OrderManagement from '../components/OrderManagement';
import ViewOrder from '../components/ViewOrder';
import ProductsTable from '../components/inventory/ProductsTable';
import BrandManagement from '../components/inventory/BrandManagement';
import WebsiteProductsTable from '../components/website/WebsiteProductsTable';
import JournalPostsTable from '../components/journal/JournalPostsTable';
import JournalPostEditor from '../components/journal/JournalPostEditor';
import ProductIntelligence from '../components/website/ProductIntelligence';
import SiteContentPage from '../components/website/SiteContentPage';
import PurchaseOrders from '../components/purchase-orders/PurchaseOrders';
import ImageManagement from '../components/ImageManagement/ImageManagement';
import Reports from '../components/Reports';
import Settings from '../components/Settings/Settings';
import AgentManagement from '../components/agents/AgentManagement';
import Messaging from '../components/Messaging/Messaging';
import Warehouse from '../components/Warehouse';
import PackingListsPage from '../components/warehouse/PackingListsPage';
import Couriers from '../components/Couriers';
import Deliveries from '../components/Deliveries';
import DeliveriesTable from '../components/deliveries/DeliveriesTable';
import DeliveryManagement from '../components/deliveries/DeliveryManagement';
import InvoicesTable from '../components/invoices/InvoicesTable';
import ViewInvoice from '../components/invoices/ViewInvoice';

// Dev mode - bypass auth
const DEV_MODE = import.meta.env.DEV;
const DEV_USER: Agent = {
  id: 'dev-admin',
  name: 'Dev Admin',
  is_admin: true,
  commission_rate: 0,
  brands: [],
  active: true,
  email: null,
  portal_access: false,
  created_at: new Date().toISOString(),
  updated_at: new Date().toISOString(),
};

export default function MasterLayout() {
  const location = useLocation();
  const navigate = useNavigate();
  const isDashboardRoute = location.pathname === '/' || location.pathname === '/dashboard';
  const [user, setUser] = useState<Agent | null>(DEV_MODE ? DEV_USER : null);
  const [loading, setLoading] = useState(!DEV_MODE);
  const [notifications, setNotifications] = useState<Notification[]>([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [unreadMessages, setUnreadMessages] = useState(0);

  const { isLoading, message } = useLoader();

  // Check authentication on mount (skip in dev mode)
  useEffect(() => {
    if (DEV_MODE) {
      setLoading(false);
      return;
    }

    const checkAuth = async () => {
      try {
        const currentUser = await authService.getCurrentAgent();
        if (currentUser) {
          setUser(currentUser);
        } else {
          navigate('/login');
        }
      } catch (error) {
        console.error('Auth check failed:', error);
        navigate('/login');
      } finally {
        setLoading(false);
      }
    };
    checkAuth();
  }, [navigate]);

  // Fetch notifications + unread messages
  useEffect(() => {
    if (user && !DEV_MODE) {
      fetchNotifications();
      fetchUnreadMessages();
    }
  }, [user]);

  const fetchNotifications = async () => {
    try {
      const result = await notificationService.list({ limit: 20 });
      if (result?.notifications) {
        setNotifications(result.notifications);
        setUnreadCount(result.notifications.filter((n) => !n.is_read).length);
      }
    } catch (error) {
      console.error('Failed to fetch notifications:', error);
    }
  };

  const fetchUnreadMessages = async () => {
    try {
      const result = await messageService.getUnreadCount();
      setUnreadMessages(result.total || 0);
    } catch {
      // Endpoint may not exist yet — silently ignore
    }
  };

  const handleMarkRead = useCallback(async (id: number) => {
    try {
      await notificationService.markRead(id);
      setNotifications(prev => prev.map(n => n.id === id ? { ...n, is_read: true } : n));
      setUnreadCount(prev => Math.max(0, prev - 1));
    } catch (error) {
      console.error('Failed to mark notification as read:', error);
    }
  }, []);

  const handleMarkAllRead = useCallback(async () => {
    try {
      await notificationService.markAllRead();
      setNotifications(prev => prev.map(n => ({ ...n, is_read: true })));
      setUnreadCount(0);
    } catch (error) {
      console.error('Failed to mark all notifications as read:', error);
    }
  }, []);

  const handleNotificationClick = useCallback(async (notification: Notification) => {
    const data = notification.data as Record<string, string | number> | null;
    if (notification.type === 'order_placed' && data?.order_number) {
      // Search by order number to get the internal DB id
      try {
        const result = await orderService.list({ search: String(data.order_number), limit: 1 });
        if (result.data?.length > 0) {
          navigate(`/order/${result.data[0].id}`);
          return;
        }
      } catch {
        // Fall through to fallback
      }
    }
    if (notification.type === 'customer_created' && data?.customer_id) {
      navigate(`/customers/${data.customer_id}`);
    } else if (notification.type === 'lead_captured') {
      navigate('/enquiries');
    }
  }, [navigate]);

  // Loading state
  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-background">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary" />
      </div>
    );
  }

  // Not authenticated
  if (!user) {
    return null;
  }

  return (
    <OrderSheetProvider>
    <SidebarProvider defaultOpen={false}>
      <AppSidebar
        user={user}
        collapsible="dock"
        intent="inset"
        unreadNotifications={unreadCount}
        unreadMessages={unreadMessages}
        notifications={notifications}
        onMarkRead={handleMarkRead}
        onMarkAllRead={handleMarkAllRead}
        onNotificationClick={handleNotificationClick}
      />
      <SidebarInset className="bg-[var(--surface-app)]">

        {/* Main Content */}
        <div className="md:hidden flex h-12 items-center px-4">
          <SidebarTrigger aria-label="Open navigation" />
        </div>
        <div className={`flex-1 overflow-auto min-h-screen text-foreground relative ${isDashboardRoute ? '' : 'p-4 lg:p-6'}`}>
          <Routes>
            {/* Dashboard */}
            <Route path="/dashboard" element={<Dashboard />} />

            {/* Customer Routes */}
            <Route path="/customers" element={<CustomersTable />} />
            <Route path="/customers/map" element={<CustomerMap />} />
            <Route path="/customers/:customerId" element={<CustomerDetail />} />

            {/* Enquiry Routes */}
            <Route path="/enquiries" element={<EnquiryList />} />
            <Route path="/enquiries/:enquiryId" element={<ViewEnquiry />} />

            {/* Order Management Routes */}
            <Route path="/orders" element={<OrdersTable />} />
            <Route path="/orders/management" element={<OrderManagement />} />
            <Route path="/order/:orderId" element={<ViewOrder />} />
            <Route path="/order-detail/:orderId" element={<OrderDetail />} />

            {/* Photo Quotes — built here, opened by the customer in the Trade Portal */}
            <Route path="/quotes" element={<QuotesTable />} />
            <Route path="/quotes/new" element={<QuoteBuilder />} />
            <Route path="/quotes/:quoteId" element={<QuoteDetail />} />
            <Route path="/quotes/:quoteId/edit" element={<QuoteBuilder />} />

            {/* Agent Performance */}
            <Route path="/agents" element={<AgentManagement />} />

            {/* Inventory Management Routes */}
            <Route path="/inventory/products" element={<ProductsTable />} />
            <Route path="/inventory/brands" element={<BrandManagement />} />

            {/* Website Management Routes */}
            <Route path="/website/products" element={<WebsiteProductsTable />} />
            <Route path="/website/journal" element={<JournalPostsTable />} />
            <Route path="/website/journal/new" element={<JournalPostEditor />} />
            <Route path="/website/journal/:id" element={<JournalPostEditor />} />
            <Route path="/website/site-content" element={<SiteContentPage />} />
            <Route path="/website/intelligence" element={<ProductIntelligence />} />
            {/* Reports */}
            <Route path="/reports" element={<Reports />} />

            {/* Finance */}
            <Route path="/finance/invoices" element={<InvoicesTable />} />
            <Route path="/finance/invoices/:invoiceId" element={<ViewInvoice />} />
            <Route path="/finance/purchase-orders" element={<PurchaseOrders />} />

            {/* Shipping Routes */}
            <Route path="/shipping/warehouse" element={<Warehouse />} />
            <Route path="/shipping/packing-lists" element={<PackingListsPage />} />
            <Route path="/shipping/couriers" element={<Couriers />} />
            <Route path="/shipping/deliveries" element={<DeliveriesTable />} />
            <Route path="/shipping/delivery-management" element={<DeliveryManagement />} />
            <Route path="/shipping/live-deliveries" element={<Navigate to="/shipping/delivery-management" replace />} />
            <Route path="/shipping/delivery-archive" element={<Navigate to="/shipping/deliveries?view=archive" replace />} />

            {/* Image Management */}
            <Route path="/image-management" element={<ImageManagement />} />

            {/* Messaging Routes */}
            <Route path="/messaging" element={<Messaging />} />

            {/* Settings Routes */}
            <Route path="/settings" element={<Settings />} />
            <Route path="/settings/*" element={<Settings />} />

            {/* Default Routes */}
            <Route index element={<Dashboard />} />
            <Route
              path="*"
              element={
                <div className="text-foreground p-8">Page not found</div>
              }
            />
          </Routes>
        </div>
      </SidebarInset>

      {/* Offline Status Indicator */}
      <OfflineStatus />

      {/* Global Progress Loader */}
      <ProgressLoader isVisible={isLoading} message={message} />
    </SidebarProvider>
    </OrderSheetProvider>
  );
}
